import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { toast } from 'sonner';
import { BaseButton, ConfirmationModal } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { VipLevelModel, VipLevelRewardModel, VipTaskItemModel, VipTaskModel } from '@shared/types';

import {
  useDeleteVipLevelMutation,
  useDeleteVipLevelRewardMutation,
  useDeleteVipTaskItemMutation,
  useDeleteVipTaskMutation,
  useLazyExportVipTaskItemParticipantsQuery,
} from '@services';

import {
  AppDispatch,
  deleteVipLevel as deleteVipLevelAction,
  deleteVipLevelRewards as deleteVipLevelRewardsAction,
  deleteVipTask as deleteVipTaskAction,
  deleteVipTaskItems as deleteVipTaskItemsAction,
} from '@store';

import { VipSystemTabsEnum } from './constants';
import { TelegramChatSettingsTab } from './TelegramChatSettingsTab';
import { UsersTable } from './UsersTable';
import { VipLevelList } from './VipLevelList';
import {
  VipLevelCreateModal,
  VipLevelEditModal,
  VipLevelRewardCreateModal,
  VipLevelRewardEditModal,
  VipTaskCreateModal,
  VipTaskEditModal,
  VipTaskItemCreateModal,
  VipTaskItemEditModal,
  VipTaskItemValidateModal,
} from './VipModals';
import { VipSettingsPageTabs } from './VipSettingsPageTabs';
import { VipTaskList } from './VipTaskList';
import { VipWheelSettingsTab } from './VipWheelSettingsTab';

import styles from './VipSettingsPage.module.css';

import { PageLayout } from 'v2/components/pageLayout/PageLayout';

const { vipSystemSettingsHeader } = styles;

export const VipSettingsPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [deleteVipTask, { isLoading: isTaskEventDeleting }] = useDeleteVipTaskMutation();
  const [deleteVipItem, { isLoading: isItemDeleting }] = useDeleteVipTaskItemMutation();
  const [exportVipTaskItems] = useLazyExportVipTaskItemParticipantsQuery();

  const [deleteVipLevel, { isLoading: isLevelEventDeleting }] = useDeleteVipLevelMutation();
  const [deleteVipLevelReward, { isLoading: isLevelRewardDeleting }] = useDeleteVipLevelRewardMutation();

  const [currentTab, setCurrentTab] = useState<VipSystemTabsEnum>(VipSystemTabsEnum.TASKS_SETTINGS);

  const [isTaskCreateModalOpened, setIsTaskCreateModalOpened] = useState<boolean>(false);
  const [isTaskEditModalOpened, setIsTaskEditModalOpened] = useState<boolean>(false);
  const [isTaskDeleteModalOpened, setIsTaskDeleteModalOpened] = useState<boolean>(false);

  const [isItemCreateModalOpened, setIsItemCreateModalOpened] = useState<boolean>(false);
  const [isItemEditModalOpened, setIsItemEditModalOpened] = useState<boolean>(false);
  const [isItemDeleteModalOpened, setIsItemDeleteModalOpened] = useState<boolean>(false);
  const [isItemValidateModalOpened, setIsItemValidateModalOpened] = useState<boolean>(false);

  const [currentTask, setCurrentTask] = useState<VipTaskModel>({} as VipTaskModel);

  const [currentTaskItem, setCurrentTaskItem] = useState<VipTaskItemModel>({} as VipTaskItemModel);

  const [isLevelCreateModalOpened, setIsLevelCreateModalOpened] = useState<boolean>(false);
  const [isLevelEditModalOpened, setIsLevelEditModalOpened] = useState<boolean>(false);
  const [isLevelDeleteModalOpened, setIsLevelDeleteModalOpened] = useState<boolean>(false);

  const [isLevelRewardCreateModalOpened, setIsLevelRewardCreateModalOpened] = useState<boolean>(false);
  const [isLevelRewardEditModalOpened, setIsLevelRewardEditModalOpened] = useState<boolean>(false);
  const [isLevelRewardDeleteModalOpened, setIsLevelRewardDeleteModalOpened] = useState<boolean>(false);

  const [currentLevel, setCurrentLevel] = useState<VipLevelModel>({} as VipLevelModel);

  const [currentLevelReward, setCurrentLevelReward] = useState<VipLevelRewardModel>({} as VipLevelRewardModel);

  const handleVipTaskDelete = async () => {
    const deletedTask = await deleteVipTask(currentTask.id).unwrap();

    dispatch(deleteVipTaskAction(deletedTask.id));

    toast.success('Success: VIP task has been deleted!');

    handleVipTaskDeleteModalClose();
  };

  const handleVipTaskItemDelete = async () => {
    const { id: taskItemId } = currentTaskItem;
    const { id: taskId } = currentTask;

    await deleteVipItem(taskItemId);

    dispatch(
      deleteVipTaskItemsAction({
        vipTaskId: taskId,
        vipTaskItemId: taskItemId,
      })
    );

    toast.success('Success: VIP task item has been deleted!');

    handleVipTaskItemDeleteModalClose();
  };

  const handleVipTaskCreateModalOpen = () => {
    setIsTaskCreateModalOpened(true);
  };

  const handleVipTaskCreateModalClose = () => {
    setIsTaskCreateModalOpened(false);
  };

  const handleVipTaskEditModalOpen = (vipTask: VipTaskModel) => {
    setCurrentTask(vipTask);

    setIsTaskEditModalOpened(true);
  };

  const handleVipTaskEditModalClose = () => {
    setCurrentTask({} as VipTaskModel);

    setIsTaskEditModalOpened(false);
  };

  const handleVipTaskDeleteModalOpen = (vipTask: VipTaskModel) => {
    setCurrentTask(vipTask);

    setIsTaskDeleteModalOpened(true);
  };

  const handleVipTaskDeleteModalClose = () => {
    setCurrentTask({} as VipTaskModel);

    setIsTaskDeleteModalOpened(false);
  };

  const handleVipTaskItemCreateModalOpen = (vipTask: VipTaskModel) => {
    setCurrentTask(vipTask);

    setIsItemCreateModalOpened(true);
  };

  const handleVipTaskItemCreateModalClose = () => {
    setCurrentTask({} as VipTaskModel);

    setIsItemCreateModalOpened(false);
  };

  const handleVipTaskItemEditModalOpen = (vipTask: VipTaskModel, vipTaskItem: VipTaskItemModel) => {
    setCurrentTask(vipTask);
    setCurrentTaskItem(vipTaskItem);

    setIsItemEditModalOpened(true);
  };

  const handleVipTaskItemEditModalClose = () => {
    setCurrentTaskItem({} as VipTaskItemModel);

    setIsItemEditModalOpened(false);
  };

  const handleVipTaskItemDeleteModalOpen = (vipTask: VipTaskModel, vipTaskItem: VipTaskItemModel) => {
    setCurrentTaskItem(vipTaskItem);
    setCurrentTask(vipTask);

    setIsItemDeleteModalOpened(true);
  };

  const handleVipTaskItemDeleteModalClose = () => {
    setCurrentTaskItem({} as VipTaskItemModel);

    setIsItemDeleteModalOpened(false);
  };

  const handleVipTaskItemExport = async (vipTaskItemId: string) => {
    await exportVipTaskItems(vipTaskItemId);
  };

  const handleVipTaskItemValidateModalOpen = async (vipTaskItem: VipTaskItemModel) => {
    setCurrentTaskItem(vipTaskItem);

    setIsItemValidateModalOpened(true);
  };

  const handleVipTaskItemValidateModalClose = () => {
    setCurrentTaskItem({} as VipTaskItemModel);

    setIsItemValidateModalOpened(false);
  };

  const handleVipLevelEditModalOpen = (vipLevel: VipLevelModel) => {
    setCurrentLevel(vipLevel);

    setIsLevelEditModalOpened(true);
  };

  const handleVipLevelDeleteModalOpen = (vipLevel: VipLevelModel) => {
    setCurrentLevel(vipLevel);

    setIsLevelDeleteModalOpened(true);
  };

  const handleVipLevelRewardEditModalOpen = (vipLevelReward: VipLevelRewardModel, vipLevel: VipLevelModel) => {
    setCurrentLevelReward(vipLevelReward);
    setCurrentLevel(vipLevel);

    setIsLevelRewardEditModalOpened(true);
  };

  const handleVipLevelRewardDeleteModalOpen = (vipLevelReward: VipLevelRewardModel) => {
    setCurrentLevelReward(vipLevelReward);

    setIsLevelRewardDeleteModalOpened(true);
  };

  const handleVipLevelRewardCreateModalOpen = (vipLevel: VipLevelModel) => {
    setCurrentLevel(vipLevel);

    setIsLevelRewardCreateModalOpened(true);
  };

  const handleVipLevelCreateModalOpen = () => {
    setIsLevelCreateModalOpened(true);
  };

  const handleVipLevelCreateModalClose = () => {
    setIsLevelCreateModalOpened(false);
  };

  const handleVipLevelEditModalClose = () => {
    setCurrentLevel({} as VipLevelModel);

    setIsLevelEditModalOpened(false);
  };

  const handleVipLevelRewardEditModalClose = () => {
    setCurrentLevelReward({} as VipLevelRewardModel);

    setIsLevelRewardEditModalOpened(false);
  };

  const handleVipLevelRewardCreateModalClose = () => {
    setCurrentLevelReward({} as VipLevelRewardModel);

    setIsLevelRewardCreateModalOpened(false);
  };

  const handleVipLevelDeleteModalClose = () => {
    setCurrentLevel({} as VipLevelModel);

    setIsLevelDeleteModalOpened(false);
  };

  const handleVipLevelDelete = async () => {
    const deletedLevel = await deleteVipLevel(currentLevel.id).unwrap();

    dispatch(deleteVipLevelAction(deletedLevel.id));

    toast.success('Success: VIP level has been deleted!');

    handleVipLevelDeleteModalClose();
  };

  const handleVipLevelRewardDelete = async () => {
    const { id: levelRewardId } = currentLevelReward;
    const { id: levelId } = currentLevel;

    await deleteVipLevelReward(levelRewardId);

    dispatch(
      deleteVipLevelRewardsAction({
        vipLevelId: levelId,
        vipLevelRewardId: levelRewardId,
      })
    );

    toast.success('Success: VIP level reward has been deleted!');

    handleVipLevelRewardDeleteModalClose();
  };

  const handleVipLevelRewardDeleteModalClose = () => {
    setCurrentLevelReward({} as VipLevelRewardModel);

    setIsLevelRewardDeleteModalOpened(false);
  };

  const setTab = (value: VipSystemTabsEnum) => {
    setCurrentTab(value);
  };

  const renderContent = () => {
    switch (currentTab) {
      case VipSystemTabsEnum.TASKS_SETTINGS: {
        return (
          <VipTaskList
            handleVipTaskEditModalOpen={handleVipTaskEditModalOpen}
            handleVipTaskDeleteModalOpen={handleVipTaskDeleteModalOpen}
            handleVipTaskItemEditModalOpen={handleVipTaskItemEditModalOpen}
            handleVipTaskItemDeleteModalOpen={handleVipTaskItemDeleteModalOpen}
            handleVipTaskItemCreateModalOpen={handleVipTaskItemCreateModalOpen}
            handleVipTaskItemExport={handleVipTaskItemExport}
            handleVipTaskItemValidateModalOpen={handleVipTaskItemValidateModalOpen}
          />
        );
      }
      case VipSystemTabsEnum.LEVELS_SETTINGS: {
        return (
          <VipLevelList
            handleVipLevelEditModalOpen={handleVipLevelEditModalOpen}
            handleVipLevelDeleteModalOpen={handleVipLevelDeleteModalOpen}
            handleVipLevelRewardEditModalOpen={handleVipLevelRewardEditModalOpen}
            handleVipLevelRewardDeleteModalOpen={handleVipLevelRewardDeleteModalOpen}
            handleVipLevelRewardCreateModalOpen={handleVipLevelRewardCreateModalOpen}
          />
        );
      }
      case VipSystemTabsEnum.USERS_SETTINGS: {
        return <UsersTable />;
      }
      case VipSystemTabsEnum.VIP_CHAT: {
        return <TelegramChatSettingsTab />;
      }
      case VipSystemTabsEnum.WHEEL_SETTINGS: {
        return <VipWheelSettingsTab />;
      }
      default: {
        return null;
      }
    }
  };

  const getTopButtonTitle = () => {
    switch (currentTab) {
      case VipSystemTabsEnum.TASKS_SETTINGS: {
        return 'Create VIP task';
      }
      case VipSystemTabsEnum.LEVELS_SETTINGS: {
        return 'Create VIP level';
      }
    }
  };

  const handleTopButtonClick = () => {
    switch (currentTab) {
      case VipSystemTabsEnum.TASKS_SETTINGS: {
        return handleVipTaskCreateModalOpen();
      }
      case VipSystemTabsEnum.LEVELS_SETTINGS: {
        return handleVipLevelCreateModalOpen();
      }
    }
  };

  return (
    <>
      <PageLayout>
        <Box className={vipSystemSettingsHeader}>
          <Typography margin="10px" fontSize="24px" fontWeight={600}>
            VIP System Settings
          </Typography>
          <BaseButton onClick={handleTopButtonClick}>{getTopButtonTitle()}</BaseButton>
        </Box>
        <VipSettingsPageTabs setTab={setTab} currentTab={currentTab} />
        {renderContent()}
      </PageLayout>
      <VipTaskCreateModal isOpened={isTaskCreateModalOpened} handleClose={handleVipTaskCreateModalClose} />
      <VipTaskEditModal
        vipTask={currentTask}
        isOpened={isTaskEditModalOpened}
        handleClose={handleVipTaskEditModalClose}
      />
      <VipTaskItemCreateModal
        vipTaskId={currentTask.id}
        vipTaskType={currentTask.type}
        handleClose={handleVipTaskItemCreateModalClose}
        isOpened={isItemCreateModalOpened}
      />
      <VipTaskItemValidateModal
        isOpened={isItemValidateModalOpened}
        vipTaskItem={currentTaskItem}
        handleClose={handleVipTaskItemValidateModalClose}
      />
      <VipTaskItemEditModal
        vipTaskItem={currentTaskItem}
        vipTaskType={currentTask.type}
        isOpened={isItemEditModalOpened}
        handleClose={handleVipTaskItemEditModalClose}
      />
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete VIP task"
        onSubmit={handleVipTaskDelete}
        isOpened={isTaskDeleteModalOpened}
        isLoading={isTaskEventDeleting}
        handleClose={handleVipTaskDeleteModalClose}
      >
        Are you sure that you want to delete this VIP task?
      </ConfirmationModal>
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete VIP task item"
        onSubmit={handleVipTaskItemDelete}
        isOpened={isItemDeleteModalOpened}
        isLoading={isItemDeleting}
        handleClose={handleVipTaskItemDeleteModalClose}
      >
        Are you sure that you want to delete this VIP task item?
      </ConfirmationModal>
      <VipLevelCreateModal isOpened={isLevelCreateModalOpened} handleClose={handleVipLevelCreateModalClose} />
      <VipLevelEditModal
        vipLevel={currentLevel}
        isOpened={isLevelEditModalOpened}
        handleClose={handleVipLevelEditModalClose}
      />
      <VipLevelRewardCreateModal
        vipLevelId={currentLevel.id}
        handleClose={handleVipLevelRewardCreateModalClose}
        isOpened={isLevelRewardCreateModalOpened}
      />
      <VipLevelRewardEditModal
        vipLevelReward={currentLevelReward}
        vipLevelId={currentLevel.id}
        isOpened={isLevelRewardEditModalOpened}
        handleClose={handleVipLevelRewardEditModalClose}
      />
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete VIP level"
        onSubmit={handleVipLevelDelete}
        isOpened={isLevelDeleteModalOpened}
        isLoading={isLevelEventDeleting}
        handleClose={handleVipLevelDeleteModalClose}
      >
        Are you sure that you want to delete this VIP level?
      </ConfirmationModal>
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete VIP level reward"
        onSubmit={handleVipLevelRewardDelete}
        isOpened={isLevelRewardDeleteModalOpened}
        isLoading={isLevelRewardDeleting}
        handleClose={handleVipLevelRewardDeleteModalClose}
      >
        Are you sure that you want to delete this VIP level reward?
      </ConfirmationModal>
    </>
  );
};
